import { SheetSettings } from "./SheetSettings";
import { useAuthoringServices } from "./services";
import { OutputPresets } from "./OutputPresets";
import { OutputLibrary } from "./OutputLibrary";
import { InsetsControl } from "./InsetsControl";
import { PageMarks } from "./PageMarks";
import { PdfArtBox } from "./PdfArtBox";
import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Printer,
  FileImage,
  FileText,
  Copy,
  Trash2,
  CheckCircle2,
} from "lucide-react";
import {
  outputSchema,
  productionSchema,
  createPrintProfile,
  MAX_PRINT_PROFILE_BYTES,
  type PrintProfile,
  uid,
  type OutputProfile,
  type Project,
  outputRoles,
  outputRoleLabels,
  createOutputProfile,
  configureOutputRole,
  migrateOutputProfiles,
  isProofOutput,
  retainOutputPresets,
} from "@proof/core";
import { Button, Control, Toggle } from "./components";
import type { EditorProps } from "./Configuration";
export function PrintProfileControl({
  value,
  onChange,
}: {
  value?: PrintProfile;
  onChange: (value: PrintProfile | undefined) => void;
}) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    generation = useRef(0),
    alive = useRef(true),
    latest = useRef(onChange);
  latest.current = onChange;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      generation.current++;
    };
  }, []);
  return (
    <section
      aria-label="Printer color profile"
      style={{ minWidth: 0, overflowWrap: "anywhere" }}
    >
      <Control label="Upload printer ICC profile">
        <input
          type="file"
          accept=".icc,.icm,application/vnd.iccprofile"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            const token = ++generation.current;
            setBusy(true);
            setError("");
            void (async () => {
              try {
                if (file.size > MAX_PRINT_PROFILE_BYTES)
                  throw new Error(
                    "Choose a CMYK printer ICC profile up to 4 MiB.",
                  );
                const profile = createPrintProfile(
                  new Uint8Array(await file.arrayBuffer()),
                  file.name.replace(/\.(icc|icm)$/i, "").slice(0, 120) ||
                    "CMYK printer profile",
                );
                if (alive.current && token === generation.current)
                  latest.current(profile);
              } catch {
                if (alive.current && token === generation.current)
                  setError(
                    "Choose a valid ICC v2/v4 CMYK printer profile up to 4 MiB.",
                  );
              } finally {
                if (alive.current && token === generation.current)
                  setBusy(false);
              }
            })();
          }}
        />
      </Control>
      {busy && <p role="status">Reading printer profile…</p>}
      {value ? (
        <>
          <p>
            Selected printer profile: <strong>{value.name}</strong>
          </p>
          <details>
            <summary>Profile identity</summary>
            <code>{value.sha256}</code>
          </details>
          <Button
            disabled={busy}
            onClick={() => {
              generation.current++;
              latest.current(undefined);
              setError("");
            }}
          >
            Remove printer profile
          </Button>
        </>
      ) : (
        <p className="muted">
          No printer profile selected. CMYK uses the generic conversion; upload
          your printer's profile for consistent color-managed vector and image
          output.
        </p>
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
export function Outputs({
  project: p,
  onChange,
  notify,
  mode = "project",
}: EditorProps & { mode?: "project" | "definition" | "library" }) {
  const services = useAuthoringServices();
  const [converting, setConverting] = useState(false);
  const currentProject = useRef<Project | undefined>(p);
  currentProject.current = p;
  useEffect(
    () => () => {
      currentProject.current = undefined;
    },
    [],
  );
  const convert = async () => {
    if (converting) return;
    setConverting(true);
    try {
      let next = migrateOutputProfiles(p, true);
      if (services.saveResource)
        next = await retainOutputPresets(
          next,
          (await services.listResources?.()) || [],
          services.saveResource,
        );
      if (currentProject.current !== p)
        throw new Error(
          "Document changed while preserving output presets. Try again.",
        );
      onChange(next);
      notify(
        "Four output stages configured; original options are available as reusable presets.",
      );
    } catch (error) {
      if (currentProject.current) notify((error as Error).message);
    } finally {
      if (currentProject.current) setConverting(false);
    }
  };
  const [selected, setSelected] = useState(p.outputs[0].id);
  const output = p.outputs.find((o) => o.id === selected) || p.outputs[0];
  const change = (patch: Partial<OutputProfile>) => {
    if (!("outputPreset" in patch)) patch.outputPreset = undefined;
    if ("production" in patch && !("productionProfile" in patch))
      patch.productionProfile = undefined;
    if ("imposition" in patch && !("sheetProfile" in patch))
      patch.sheetProfile = undefined;
    try {
      onChange({
        ...p,
        outputs: p.outputs.map((o) =>
          o.id === output.id ? outputSchema.parse({ ...o, ...patch }) : o,
        ),
      });
    } catch (error) {
      notify((error as Error).message);
    }
  };
  const library = (
    <OutputLibrary
      project={p}
      output={output}
      onChange={onChange}
      notify={notify}
      canAssign={mode !== "library"}
      renderEditor={(definition, changeDefinition) => (
        <Outputs
          mode="definition"
          project={{ ...p, outputs: [definition], archivedOutputs: undefined }}
          onChange={(next) => changeDefinition(next.outputs[0])}
          notify={notify}
        />
      )}
    />
  );
  if (mode === "library")
    return (
      <div className="outputs-page">
        <h1>Output profiles</h1>
        {library}
      </div>
    );
  return (
    <div className="outputs-page">
      <div className="page-intro">
        <div>
          <span className="eyebrow">FROM SCREEN TO PAPER</span>
          {mode === "project" ? (
            <h1>Output profiles</h1>
          ) : (
            <h3>Global profile settings</h3>
          )}
          <p>
            {mode === "project"
              ? `Assign global profiles to ${p.name}'s output stages. Stage settings are saved with this project.`
              : "Define reusable output settings for projects to assign."}
          </p>
        </div>
        {mode === "project" && (
          <Button
            kind="primary"
            disabled={p.outputs.length >= 4}
            onClick={() => {
              const role = outputRoles.find(
                (role) => !p.outputs.some((o) => o.role === role),
              );
              if (!role || p.outputs.length >= 4) return;
              const o = createOutputProfile(role);
              onChange({ ...p, outputs: [...p.outputs, o] });
              setSelected(o.id);
            }}
          >
            <Plus size={16} />
            Add output stage
          </Button>
        )}
      </div>
      {mode === "project" && library}
      {mode === "project" && (
        <p>
          Up to four output stages: Preview / Proof, Proof Download, High Res
          and Print.
        </p>
      )}
      {mode === "project" && p.outputs.some((o) => !o.role) && (
        <div className="note-box">
          <p>
            Configure four output stages and preserve every original option as a
            reusable output preset.
          </p>
          <Button disabled={converting} onClick={() => void convert()}>
            Set up four output stages
          </Button>
        </div>
      )}
      <div className="output-layout">
        <div className="output-cards">
          {p.outputs.map((o) => (
            <button
              key={o.id}
              className={`output-profile-card ${output.id === o.id ? "selected" : ""}`}
              onClick={() => setSelected(o.id)}
            >
              <div className="output-icon">
                {o.format === "pdf" ? (
                  <FileText size={24} />
                ) : (
                  <FileImage size={24} />
                )}
              </div>
              <h3>{o.name}</h3>
              {o.role && (
                <span className="badge green">{outputRoleLabels[o.role]}</span>
              )}
              <p>
                {o.format.toUpperCase()} · {o.colorSpace.toUpperCase()} ·{" "}
                {o.dpi} DPI
              </p>
              <div>
                <span className="badge">
                  {o.imposition.enabled ? "Sheet layout" : "Individual pages"}
                </span>
                {o.id === p.outputs[0].id && (
                  <span className="badge green">Default</span>
                )}
              </div>
            </button>
          ))}
        </div>
        <section className="panel output-inspector">
          <header>
            <div>
              <span className="eyebrow">PROFILE SETTINGS</span>
              <h3>{output.name}</h3>
            </div>
            <Printer size={20} />
          </header>
          <div className="inspector-body">
            <Control label="Output stage">
              <select
                value={output.role || ""}
                onChange={(e) => {
                  if (e.target.value)
                    change(
                      configureOutputRole(
                        output,
                        e.target.value as NonNullable<OutputProfile["role"]>,
                      ),
                    );
                }}
              >
                <option value="" disabled>
                  Choose an output stage
                </option>
                {outputRoles.map((role) => (
                  <option
                    key={role}
                    value={role}
                    disabled={p.outputs.some(
                      (o) => o.id !== output.id && o.role === role,
                    )}
                  >
                    {outputRoleLabels[role]}
                  </option>
                ))}
              </select>
            </Control>
            <Control label="Profile name">
              <input
                value={output.name}
                onChange={(e) => change({ name: e.target.value })}
              />
            </Control>
            {mode === "project" && (
              <OutputPresets
                project={p}
                output={output}
                onChange={onChange}
                notify={notify}
              />
            )}
            <div className="control-grid">
              <Control label="File format">
                <select
                  value={output.format}
                  onChange={(e) => {
                    const format = e.target.value as OutputProfile["format"];
                    change({
                      format,
                      ...(format !== "pdf"
                        ? {
                            preserveImportedPdf: undefined,
                            preserveImportedPaint: undefined,
                          }
                        : {}),
                      ...(format === "png" || format === "svg"
                        ? { colorSpace: "rgb" as const }
                        : {}),
                    });
                  }}
                >
                  {["pdf", "svg", "png", "jpeg", "tiff"]
                    .filter((f) => output.role !== "proof" || f !== "svg")
                    .map((f) => (
                      <option key={f} value={f}>
                        {f.toUpperCase()}
                      </option>
                    ))}
                </select>
              </Control>
              <Control label="Color space">
                <select
                  disabled={isProofOutput(output)}
                  value={output.colorSpace}
                  onChange={(e) =>
                    change({
                      colorSpace: e.target.value as OutputProfile["colorSpace"],
                      ...(e.target.value === "cmyk"
                        ? {
                            preserveImportedPdf: undefined,
                            preserveImportedPaint: undefined,
                          }
                        : {}),
                    })
                  }
                >
                  <option value="rgb">RGB</option>
                  {!["png", "svg"].includes(output.format) && (
                    <option value="cmyk">CMYK</option>
                  )}
                </select>
              </Control>
              <Control label="Resolution (DPI)">
                <input
                  type="number"
                  min="36"
                  max="1200"
                  step="1"
                  value={output.dpi}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (n >= 36 && n <= 1200) change({ dpi: n });
                  }}
                />
              </Control>
              {output.format === "pdf" && output.colorSpace === "rgb" && (
                <Toggle
                  label="Preserve imported CMYK paint"
                  value={!!output.preserveImportedPaint}
                  onChange={(preserveImportedPaint) =>
                    change({ preserveImportedPaint })
                  }
                />
              )}
              {output.format === "pdf" && output.colorSpace === "rgb" && (
                <Toggle
                  label="Preserve imported PDF colors and vectors"
                  value={!!output.preserveImportedPdf}
                  onChange={(preserveImportedPdf) =>
                    change({ preserveImportedPdf })
                  }
                />
              )}
              {output.format === "pdf" && (
                <Toggle
                  label="Absolute PDF text positions"
                  value={!!output.absoluteTextPositions}
                  onChange={(absoluteTextPositions) =>
                    change({
                      absoluteTextPositions: absoluteTextPositions || undefined,
                    })
                  }
                />
              )}
              {output.format === "pdf" && output.colorSpace === "rgb" && (
                <Control label="PDF RGB channel precision">
                  <select
                    value={output.rgbPrecision ?? "default"}
                    onChange={(e) =>
                      change({
                        rgbPrecision:
                          e.target.value === "default"
                            ? undefined
                            : Number(e.target.value),
                      })
                    }
                  >
                    <option value="default">Default</option>
                    {[3, 4, 5, 6].map((n) => (
                      <option key={n} value={n}>
                        {n} decimal places
                      </option>
                    ))}
                  </select>
                </Control>
              )}
              <Toggle
                label="Include page bleed, slug and marks"
                value={output.includeProduction !== false}
                onChange={(includeProduction) => change({ includeProduction })}
              />
              <Control label="JPEG quality">
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={output.quality}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (n >= 1 && n <= 100) change({ quality: n });
                  }}
                />
              </Control>
            </div>
            {output.format === "pdf" && (
              <PdfArtBox
                value={output.artBox}
                width={
                  output.imposition.enabled
                    ? output.imposition.sheetWidth
                    : p.pages[0]?.width || 612
                }
                height={
                  output.imposition.enabled
                    ? output.imposition.sheetHeight
                    : p.pages[0]?.height || 792
                }
                profile
                onChange={(artBox) => change({ artBox })}
              />
            )}
            <details>
              <summary>Profile bleed and marks</summary>
              <Toggle
                label="Override page production settings"
                value={!!output.production}
                onChange={(enabled) =>
                  change({
                    production: enabled
                      ? structuredClone(
                          p.pages[0]?.production || productionSchema.parse({}),
                        )
                      : undefined,
                  })
                }
              />
              {output.production && (
                <fieldset
                  disabled={output.includeProduction === false}
                  style={{ border: 0, padding: 0, minWidth: 0 }}
                >
                  <p className="muted">
                    These settings apply to every logical page for this profile.
                    Other profiles can inherit the document's page settings.
                  </p>
                  <InsetsControl
                    label="Profile bleed"
                    value={output.production.bleed}
                    max={144}
                    onChange={(bleed) =>
                      change({ production: { ...output.production!, bleed } })
                    }
                  />
                  <InsetsControl
                    label="Profile slug"
                    value={output.production.slug}
                    max={144}
                    onChange={(slug) =>
                      change({ production: { ...output.production!, slug } })
                    }
                  />
                  <Toggle
                    label="Clip profile artwork to bleed"
                    value={!!output.production.clipArtworkToBleed}
                    onChange={(clipArtworkToBleed) =>
                      change({
                        production: {
                          ...output.production!,
                          clipArtworkToBleed,
                        },
                      })
                    }
                  />
                  <PageMarks
                    value={output.production}
                    onChange={(production) => change({ production })}
                  />
                </fieldset>
              )}
              {output.production && output.includeProduction === false && (
                <p className="muted">
                  Enable Include page bleed, slug and marks to use this
                  profile's production settings.
                </p>
              )}
            </details>
            <Control label="Watermark">
              <input
                value={output.watermark}
                placeholder="Optional proof watermark"
                onChange={(e) => change({ watermark: e.target.value })}
              />
            </Control>
            <h4>Image quality policy</h4>
            <div className="control-grid">
              <Control label="Minimum effective DPI">
                <input
                  type="number"
                  min="0"
                  value={output.minDpi}
                  onChange={(e) =>
                    change({ minDpi: Math.max(0, Number(e.target.value)) })
                  }
                />
              </Control>
              <Control label="Low-resolution images">
                <select
                  value={output.lowDpiPolicy}
                  onChange={(e) =>
                    change({
                      lowDpiPolicy: e.target
                        .value as OutputProfile["lowDpiPolicy"],
                    })
                  }
                >
                  <option value="warn">Allow with warning</option>
                  <option value="block">Block generation</option>
                </select>
              </Control>
            </div>
            <Toggle
              label="Allow viewing"
              value={output.allowView}
              onChange={(v) => change({ allowView: v })}
            />
            <Toggle
              label="Allow downloading"
              value={output.allowDownload}
              onChange={(v) => change({ allowDownload: v })}
            />
            <h4>Sheet imposition</h4>
            {isProofOutput(output) ? (
              <p>
                Preview and proof downloads show individual product pages.
                Assign a sheet preset to High Res or Print.
              </p>
            ) : (
              <SheetSettings
                project={p}
                output={output}
                onChange={(imposition) => change({ imposition })}
              />
            )}
            {output.colorSpace === "cmyk" && (
              <PrintProfileControl
                key={`${p.id}:${output.id}`}
                value={output.printProfile}
                onChange={(printProfile) => change({ printProfile })}
              />
            )}
            {output.colorSpace === "cmyk" && (
              <div className="note-box">
                <Printer size={18} />
                <p>
                  CMYK PDF keeps vector text and shapes. With a printer profile,
                  vectors and images use the same ICC conversion and the PDF
                  embeds that printing condition. TIFF and JPEG retain the
                  profile. PDF/X conformance requires separate independent
                  validation.
                </p>
              </div>
            )}
            {mode === "project" && (
              <div className="inspector-footer">
                <Button
                  disabled={p.outputs.length >= 4}
                  onClick={() => {
                    const role = outputRoles.find(
                      (role) => !p.outputs.some((o) => o.role === role),
                    );
                    if (!role || p.outputs.length >= 4) return;
                    const clone = {
                      ...configureOutputRole(structuredClone(output), role),
                      id: uid("output"),
                      name: `${output.name} copy`,
                    };
                    onChange({ ...p, outputs: [...p.outputs, clone] });
                    setSelected(clone.id);
                  }}
                >
                  <Copy size={14} />
                  Duplicate
                </Button>
                <Button
                  kind="danger"
                  disabled={p.outputs.length === 1}
                  onClick={() => {
                    onChange({
                      ...p,
                      outputs: p.outputs.filter((o) => o.id !== output.id),
                    });
                    setSelected(p.outputs.find((o) => o.id !== output.id)!.id);
                  }}
                >
                  <Trash2 size={14} />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </section>
      </div>
      {mode === "project" && (
        <section className="panel padded">
          <h3>Bleed in on-screen previews</h3>
          <Toggle
            label="Allow the Show bleed checkbox"
            value={p.settings.bleedPreview?.enabled !== false}
            onChange={(enabled) =>
              onChange({
                ...p,
                settings: {
                  ...p.settings,
                  bleedPreview: {
                    enabled,
                    defaultVisible:
                      p.settings.bleedPreview?.defaultVisible || false,
                  },
                },
              })
            }
          />
          <Toggle
            label="Show bleed by default"
            value={!!p.settings.bleedPreview?.defaultVisible}
            disabled={p.settings.bleedPreview?.enabled === false}
            onChange={(defaultVisible) =>
              onChange({
                ...p,
                settings: {
                  ...p.settings,
                  bleedPreview: {
                    enabled: p.settings.bleedPreview?.enabled !== false,
                    defaultVisible,
                  },
                },
              })
            }
          />
        </section>
      )}
      {mode === "project" && !!p.archivedOutputs?.length && (
        <section className="panel padded">
          <h3>Archived output definitions</h3>
          <p>
            Restore an original definition into the selected stage; the current
            definition is archived.
          </p>
          {p.archivedOutputs.map((archived, index) => (
            <div key={`${archived.id}:${index}`} className="control-grid">
              <span>
                {archived.name} / {archived.format.toUpperCase()} /{" "}
                {archived.dpi} DPI
              </span>
              <Button
                onClick={() => {
                  const restored = output.role
                    ? configureOutputRole(archived, output.role)
                    : archived;
                  onChange({
                    ...p,
                    outputs: p.outputs.map((o) =>
                      o.id === output.id ? { ...restored, id: output.id } : o,
                    ),
                    archivedOutputs: [
                      ...p.archivedOutputs!.filter((_, i) => i !== index),
                      output,
                    ],
                  });
                }}
              >
                Restore into selected stage
              </Button>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
